import React, { useState, useEffect, useCallback, useRef } from "react";

const TankJumpingGame = () => {
  const [gameState, setGameState] = useState("demo"); // Start in demo mode
  const [tankY, setTankY] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0); // In-memory high score
  const [gameSpeed, setGameSpeed] = useState(4);

  const gameAreaRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(0);
  const tankYRef = useRef(0);
  const velocityRef = useRef(0);
  const isJumpingRef = useRef(false);

  // Game constants
  const GRAVITY = -0.8; // Negative to pull down
  const JUMP_FORCE = 15; // Positive to go up
  const GROUND_Y = 0;
  const TANK_HEIGHT = 40;
  const TANK_WIDTH = 60;
  const OBSTACLE_WIDTH = 30;
  const OBSTACLE_HEIGHT = 50;
  const GAME_HEIGHT = 300;
  const GAME_WIDTH = 800;

  // Tank component
  const Tank = ({ y }) => (
    <div
      style={{
        position: "absolute",
        left: "100px",
        bottom: `${20 + y}px`,
        width: `${TANK_WIDTH}px`,
        height: `${TANK_HEIGHT}px`,
      }}
    >
      {/* Tank body */}
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {/* Main body */}
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "8px",
            right: "8px",
            height: "24px",
            backgroundColor: "#ffffff",
          }}
        ></div>
        {/* Turret */}
        <div
          style={{
            position: "absolute",
            bottom: "16px",
            left: "16px",
            right: "16px",
            height: "16px",
            backgroundColor: "#d1d5db",
          }}
        ></div>
        {/* Cannon */}
        <div
          style={{
            position: "absolute",
            bottom: "24px",
            right: "0",
            width: "32px",
            height: "8px",
            backgroundColor: "#ffffff",
          }}
        ></div>
        {/* Tracks */}
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            right: "0",
            height: "8px",
            backgroundColor: "#ffffff",
          }}
        ></div>
        {/* Track wheels */}
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "4px",
            width: "12px",
            height: "12px",
            backgroundColor: "#9ca3af",
            borderRadius: "50%",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "0",
            right: "4px",
            width: "12px",
            height: "12px",
            backgroundColor: "#9ca3af",
            borderRadius: "50%",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "50%",
            transform: "translateX(-50%)",
            width: "12px",
            height: "12px",
            backgroundColor: "#9ca3af",
            borderRadius: "50%",
          }}
        ></div>
      </div>
    </div>
  );

  // Obstacle component
  const Obstacle = ({ x }) => (
    <div
      style={{
        position: "absolute",
        left: `${x}px`,
        bottom: "20px",
        width: `${OBSTACLE_WIDTH}px`,
        height: `${OBSTACLE_HEIGHT}px`,
      }}
    >
      {/* Rock/barrier obstacle */}
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#ffffff",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "8px",
            backgroundColor: "#d1d5db",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: "8px",
            left: "4px",
            width: "8px",
            height: "8px",
            backgroundColor: "#9ca3af",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: "16px",
            right: "4px",
            width: "12px",
            height: "12px",
            backgroundColor: "#9ca3af",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "8px",
            left: "8px",
            width: "8px",
            height: "8px",
            backgroundColor: "#9ca3af",
          }}
        ></div>
      </div>
    </div>
  );

  // Ground component
  const Ground = () => (
    <div
      style={{
        position: "absolute",
        bottom: "0",
        left: "0",
        right: "0",
        height: "20px",
        backgroundColor: "#374151",
        borderTop: "2px solid #ffffff",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, #4b5563, #1f2937)",
        }}
      ></div>
    </div>
  );

  // Collision detection
  const checkCollision = useCallback(() => {
    const tankLeft = 100;
    const tankRight = tankLeft + TANK_WIDTH;
    const tankBottom = 20 + tankYRef.current;
    const tankTop = tankBottom + TANK_HEIGHT;

    return obstacles.some((obstacle) => {
      const obstacleLeft = obstacle.x;
      const obstacleRight = obstacleLeft + OBSTACLE_WIDTH;
      const obstacleBottom = 20;
      const obstacleTop = obstacleBottom + OBSTACLE_HEIGHT;

      return (
        tankRight > obstacleLeft &&
        tankLeft < obstacleRight &&
        tankTop > obstacleBottom &&
        tankBottom < obstacleTop
      );
    });
  }, [obstacles]);

  // Demo mode logic - auto jump when obstacle is near
  const shouldAutoJump = useCallback(() => {
    if (gameState !== "demo") return false;

    const tankLeft = 100;
    const nextObstacle = obstacles.find(
      (obs) => obs.x > tankLeft && obs.x < tankLeft + 150
    );

    return (
      nextObstacle &&
      nextObstacle.x < tankLeft + 100 &&
      tankYRef.current === GROUND_Y
    );
  }, [obstacles, gameState]);

  // Jump function - optimized for responsiveness
  const jump = useCallback(() => {
    if (tankYRef.current === GROUND_Y && !isJumpingRef.current) {
      // Immediate ref updates for instant response
      velocityRef.current = JUMP_FORCE;
      isJumpingRef.current = true;

      // Force immediate visual update
      setTankY(tankYRef.current);
    }
  }, []);

  // Reset game
  const resetGame = useCallback(() => {
    tankYRef.current = GROUND_Y;
    velocityRef.current = 0;
    isJumpingRef.current = false;
    setTankY(GROUND_Y);
    setObstacles([]);
    setScore(0);
    setGameSpeed(4);
    lastTimeRef.current = 0;
  }, []);

  // Start game
  const startGame = useCallback(() => {
    resetGame();
    setGameState("playing");
  }, [resetGame]);

  // Start demo
  const startDemo = useCallback(() => {
    resetGame();
    setGameState("demo");
  }, [resetGame]);

  // Game loop
  const gameLoop = useCallback(
    (currentTime) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }

      lastTimeRef.current = currentTime;

      if (gameState === "playing" || gameState === "demo") {
        // Physics using refs to avoid React timing issues
        velocityRef.current += GRAVITY;
        tankYRef.current += velocityRef.current;

        // Keep tank above ground
        if (tankYRef.current <= GROUND_Y) {
          tankYRef.current = GROUND_Y;
          velocityRef.current = 0;
          isJumpingRef.current = false;
        }

        // Update React state for rendering
        setTankY(tankYRef.current);

        // Auto jump in demo mode
        if (shouldAutoJump()) {
          jump();
        }

        // Update obstacles
        setObstacles((prev) => {
          const updated = prev
            .map((obs) => ({ ...obs, x: obs.x - gameSpeed }))
            .filter((obs) => obs.x > -OBSTACLE_WIDTH);

          // Add new obstacles
          if (
            updated.length === 0 ||
            updated[updated.length - 1].x < GAME_WIDTH - 200
          ) {
            const shouldSpawn = Math.random() < 0.02;
            if (shouldSpawn) {
              updated.push({ x: GAME_WIDTH, id: Date.now() });
            }
          }

          return updated;
        });

        // Update score and speed
        setScore((prev) => prev + 1);
        setGameSpeed((prev) => Math.min(prev + 0.001, 8));

        // Check collision
        if (checkCollision()) {
          const finalScore = Math.floor(score / 10);
          if (finalScore > highScore) {
            setHighScore(finalScore);
          }
          // Automatically restart in demo mode
          startDemo();
        }
      }

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    },
    [
      gameState,
      shouldAutoJump,
      jump,
      gameSpeed,
      checkCollision,
      score,
      highScore,
      GRAVITY,
      startDemo,
    ]
  );

  // Start game loop
  useEffect(() => {
    if (gameState === "playing" || gameState === "demo") {
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState, gameLoop]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#000000",
        padding: "16px",
      }}
    >
      <h1
        style={{
          fontSize: "36px",
          fontWeight: "bold",
          color: "#ffffff",
          marginBottom: "16px",
        }}
      >
        Tank Jump
      </h1>

      {/* Game Area */}
      <div
        ref={gameAreaRef}
        style={{
          position: "relative",
          backgroundColor: "#000000",
          border: "4px solid #ffffff",
          overflow: "hidden",
          width: `${GAME_WIDTH}px`,
          height: `${GAME_HEIGHT}px`,
        }}
      >
        {/* Background clouds - simplified */}
        <div
          style={{
            position: "absolute",
            top: "40px",
            left: "80px",
            width: "64px",
            height: "32px",
            backgroundColor: "#374151",
            borderRadius: "50px",
            opacity: "0.6",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: "64px",
            right: "128px",
            width: "80px",
            height: "40px",
            backgroundColor: "#374151",
            borderRadius: "50px",
            opacity: "0.6",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: "32px",
            right: "240px",
            width: "48px",
            height: "24px",
            backgroundColor: "#374151",
            borderRadius: "50px",
            opacity: "0.6",
          }}
        ></div>

        {/* Ground */}
        <Ground />

        {/* Tank */}
        {(gameState === "playing" || gameState === "demo") && (
          <Tank y={tankY} />
        )}

        {/* Obstacles */}
        {(gameState === "playing" || gameState === "demo") &&
          obstacles.map((obstacle) => (
            <Obstacle key={obstacle.id} x={obstacle.x} />
          ))}

        {/* Score */}
        {(gameState === "playing" || gameState === "demo") && (
          <div
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              fontSize: "20px",
              fontWeight: "bold",
              color: "#ffffff",
              zIndex: 1,
            }}
          >
            <div>Score: {Math.floor(score / 10)}</div>
            <div style={{ fontSize: "14px" }}>High: {highScore}</div>
          </div>
        )}

        {/* Demo indicator */}
        {gameState === "demo" && (
          <div
            style={{
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: "20px",
              fontWeight: "bold",
              color: "#000000",
              backgroundColor: "#ffffff",
              padding: "8px",
              borderRadius: "4px",
              zIndex: 1,
            }}
          >
            DEMO MODE
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          marginTop: "24px",
          textAlign: "center",
        }}
      >
        {gameState === "playing" && (
          <button
            onClick={jump}
            onTouchStart={(e) => {
              e.preventDefault();
              jump();
            }}
            style={{
              padding: "24px 48px",
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "2px solid #ffffff",
              borderRadius: "8px",
              fontWeight: "bold",
              fontSize: "24px",
              cursor: "pointer",
              userSelect: "none",
              touchAction: "manipulation",
            }}
          >
            JUMP
          </button>
        )}

        {gameState === "demo" && (
          <button
            onClick={startGame}
            style={{
              padding: "24px 48px",
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "2px solid #ffffff",
              borderRadius: "8px",
              fontWeight: "bold",
              fontSize: "24px",
              cursor: "pointer",
              userSelect: "none",
              touchAction: "manipulation",
            }}
          >
            START
          </button>
        )}
      </div>
    </div>
  );
};

export default TankJumpingGame;
